import { Inject, Injectable, OnDestroy } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN, ApplicationPathConstants, MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { EngagementsUtil, PasDataLoaderService, PortfolioResolverUtil } from '@ddt-web/setup-data-workers';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { PaQueryParamKey, SharedQueryParamKey, UrlHistoryService, UrlService, UrlUtils } from '@ddt-web/url';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import { combineLatest, iif, of, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';


@Injectable()
export class CustomAdvisoryFeeService implements OnDestroy {
	private dataSubscription: Subscription;

	private noPreviousPerformanceState: boolean = false;
	private shouldReloadPerformance: boolean = false; // when portfolios or benchmark is changed

	constructor(
		private storeService: StoreService,
		private urlService: UrlService,
		private pasDataLoaderService: PasDataLoaderService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private urlHistoryService: UrlHistoryService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		@Inject(MODELS_STORE_NAME_TOKEN) public modelsStoreName: string
	) { }

	public handleCustomAdvisoryFeeUpdate(): void {
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		this.dataSubscription = this.applicationStateStoreProxyService.getPerformanceState()
			.pipe(
				distinctUntilChanged((previous: PerformanceState, current: PerformanceState) => {
					// check if the fee has never been set before (never set excludeFee or customFee)
					this.noPreviousPerformanceState = isNil(previous.includeFee) && isNil(previous.customAdvisoryFee);

					return previous.customAdvisoryFee === current.customAdvisoryFee; // should trigger this only for customAdvisoryFee
				}),
				switchMap(state => {
					return combineLatest(
						of(state),
						this.urlService.queryParams$.pipe(
							filter(queryParams => !!queryParams.portfolios || !!queryParams.engagement),
							distinctUntilChanged((previous, current) => {
								// check if the portfolios or benchmark has changed
								const isQueryParamsEqual = UrlUtils.isSpecificQueryParamsEqual(
									previous, current, [SharedQueryParamKey.PORTFOLIOS, PaQueryParamKey.AC_BENCHMARK]
								);

								const history = this.urlHistoryService.history;
								const isComingFromPortfolioEditor = history.length > 1 && history[history.length - 2].includes(ApplicationPathConstants.PORTFOLIO_EDITOR_PATH);

								// check if redirected from PE - we always need to trigger the performance call due to possible changes
								// if not redirected from PE, we need to trigger only if portfolios or acBenchmark change
								this.shouldReloadPerformance = isComingFromPortfolioEditor || !isQueryParamsEqual; // if the portfolios or benchmark has changed, we should re-trigger analysis

								return !this.shouldReloadPerformance || !!current.engagement;
							})
						)
					);
				}),
				switchMap(([state, queryParams]) => {
					const portfolioIds = [].concat(queryParams.portfolios).filter(portfolio => !!portfolio);
					const engagementId = queryParams.engagement;

					const internalPortfolios = PortfolioResolverUtil.extractInternalPortfolioIds(portfolioIds);
					const partnerPortfolios = PortfolioResolverUtil.extractPartnerPortfolioIds(portfolioIds);
					const externalPortfolios = PortfolioResolverUtil.extractExternalPortfolioIds(portfolioIds);

					return combineLatest(
						this.storeService.selectAll(this.storeConfig[this.modelsStoreName]),
						this.storeService.selectAll(this.storeConfig['associatedPortfolios']),
						iif(
							() => !!engagementId,
							this.storeService.selectAll(this.storeConfig['engagementPortfolios']),
							of(null)
						),
						of(state),
						of(queryParams)
					).pipe(
						filter(([config, associatedPortfolios, engagementPortfolios, performanceState, queryParams]: any) => {
							const isExternalPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(externalPortfolios, config, associatedPortfolios);
							const isPartnerPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(partnerPortfolios, config, associatedPortfolios);
							const isInternalPortfoliosSuccessfullyLoaded = PortfolioResolverUtil.isPortfoliosSuccessfullyLoaded(internalPortfolios, config, associatedPortfolios);

							const isPortfoliosValid = !isEmpty(portfolioIds)
								&& !associatedPortfolios.isLoading
								&& isExternalPortfoliosSuccessfullyLoaded
								&& isInternalPortfoliosSuccessfullyLoaded
								&& isPartnerPortfoliosSuccessfullyLoaded;
							const isEngagementValid = !!engagementId && !!engagementPortfolios && !engagementPortfolios.isLoading && engagementPortfolios.entities[engagementId]
								&& EngagementsUtil.areUpdatedEngagementsInAssociatedPortfolios(engagementPortfolios.entities[engagementId], associatedPortfolios, config?.entities);

							return config && !config.isLoading && (isPortfoliosValid || isEngagementValid);
						})
					);
				})
			).subscribe(([config, associatedPortfolios, engagementPortfolios, performanceState, queryParams]) => {
				const customAdvisoryFee = get(performanceState, 'customAdvisoryFee');

				if (customAdvisoryFee || this.noPreviousPerformanceState || this.shouldReloadPerformance) {
					// reset flags
					this.noPreviousPerformanceState = false;
					this.shouldReloadPerformance = false;

					const engagementId = queryParams.engagement;

					if (!!engagementId) {
						const engagementPortfolio = engagementPortfolios.entities[engagementId];
						this.pasDataLoaderService.populateStoreForEngagement(
							engagementPortfolio,
							associatedPortfolios,
							config?.entities,
							null,
							[this.storeConfig.performance],
							{
								identifierToBeUsedAsWweApiParamId: 'identifier',
								simulatedPerformanceUserInputs: { feeOverride: customAdvisoryFee ? customAdvisoryFee / 100 : null },
								currency: 'USD'
							}
						);
					} else {
						const portfolioIds = [].concat(queryParams.portfolios);
						this.pasDataLoaderService.populateStoresForPortfolios(
							portfolioIds,
							config.entities,
							associatedPortfolios,
							{
								identifierToBeUsedAsWweApiParamId: 'identifier',
								simulatedPerformanceUserInputs: { feeOverride: customAdvisoryFee ? customAdvisoryFee / 100 : null },
								currency: 'USD',
								benchmark: queryParams.acBenchmark ? { portfolioBenchmarkId: queryParams.acBenchmark } : null
							},
							queryParams.acBenchmark,
							[this.storeConfig.performance]
						);
					}
				}
			});
	}

	public ngOnDestroy(): void {
		this.dataSubscription.unsubscribe();
	}
}

